import { HttpClient } from '@angular/common/http';
import { Injectable, inject } from '@angular/core';
import { firstValueFrom } from 'rxjs';
import { environment } from 'src/environments/environment';

@Injectable({ providedIn: 'root' })
export class EncryptionService {
  private _http = inject(HttpClient);
  private _publicKey: CryptoKey | null = null;
  private _serverTime: string = '';

  async fetchServerKeyAndTime(): Promise<void> {
    const response: any = await firstValueFrom(
      this._http.get(`${environment.baseURl}/Auth/public-key?_t=${new Date().getTime()}`)
    );

    this._serverTime = response.serverTime;

    // Only re-import the key if not already cached
    if (!this._publicKey) {
      const pem = response.publicKey as string;
      const pemContents = pem
        .replace(/-----BEGIN PUBLIC KEY-----/g, '')
        .replace(/-----END PUBLIC KEY-----/g, '')
        .replace(/[\s\r\n]/g, '');

      const binaryDer = atob(pemContents);
      const derBytes = new Uint8Array(binaryDer.length);
      for (let i = 0; i < binaryDer.length; i++) {
        derBytes[i] = binaryDer.charCodeAt(i);
      }

      this._publicKey = await crypto.subtle.importKey(
        'spki',
        derBytes,
        { name: 'RSA-OAEP', hash: 'SHA-256' },
        false,
        ['encrypt']
      );
    }
  }

  async encryptPassword(password: string): Promise<string> {
    if (!this._publicKey || !this._serverTime) {
      throw new Error('Call fetchServerKeyAndTime() first.');
    }

    const payload = `${password}|${this._serverTime}`;
    const encoded = new TextEncoder().encode(payload);

    const encryptedBuffer = await crypto.subtle.encrypt(
      { name: 'RSA-OAEP' },
      this._publicKey,
      encoded
    );

    return this.arrayBufferToBase64(encryptedBuffer);
  }

  clearPublicKey(): void {
    this._publicKey = null;
    this._serverTime = '';
  }

  private arrayBufferToBase64(buffer: ArrayBuffer): string {
    const bytes = new Uint8Array(buffer);
    const chunkSize = 8192;
    let binary = '';
    for (let i = 0; i < bytes.length; i += chunkSize) {
      const chunk = bytes.subarray(i, i + chunkSize);
      binary += String.fromCharCode.apply(null, Array.from(chunk));
    }
    return btoa(binary);
  }
}
